import React, { useState, useEffect } from 'react';
import {
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { probationaryAPI } from '../services/api';

const ProbationaryManagement = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { user } = useAuth();

  // Dashboard data with proper defaults
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalActiveProbation: 0,
      reviewsDueThisMonth: 0,
      reviewsOverdue: 0,
      completedReviews: 0
    },
    recentEmployees: [],
    upcomingReviews: []
  });

  // Active employees
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [upcomingReviews, setUpcomingReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState('all'); // 'all', 'due', 'overdue', 'completed'

  const tabs = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: UserGroupIcon,
    },
    {
      id: 'active',
      name: 'Active Probations',
      icon: ClockIcon,
    },
    {
      id: 'reviews',
      name: 'Upcoming Reviews',
      icon: CalendarIcon,
    }
  ];

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboardData();
    } else if (activeTab === 'active') {
      loadActiveEmployees();
    } else if (activeTab === 'reviews') {
      loadUpcomingReviews();
    }
  }, [activeTab]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setMessage(''); // Clear any previous messages
      const response = await probationaryAPI.getDashboardData();
      const responseData = response.data.data || response.data;
      console.log('Loading dashboard data:', responseData); // Debug log
      
      // Ensure we have proper data structure
      const safeData = {
        stats: {
          totalActiveProbation: responseData?.stats?.totalActiveProbation || 0,
          reviewsDueThisMonth: responseData?.stats?.reviewsDueThisMonth || 0,
          reviewsOverdue: responseData?.stats?.reviewsOverdue || 0,
          completionRate: responseData?.stats?.completionRate || 100
        },
        recentEmployees: responseData?.recentEmployees || [],
        upcomingReviews: responseData?.upcomingReviews || []
      };
      
      setDashboardData(safeData);
      setMessage('Dashboard data refreshed successfully');
      
      // Auto-clear success message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setMessage('Error loading dashboard data. Please try again.');
      // Keep default data structure on error
      setDashboardData({
        stats: {
          totalActiveProbation: 0,
          reviewsDueThisMonth: 0,
          reviewsOverdue: 0,
          completedReviews: 0
        },
        recentEmployees: [],
        upcomingReviews: []
      });
    } finally {
      setLoading(false);
    }
  };

  const loadActiveEmployees = async () => {
    try {
      setLoading(true);
      setMessage(''); // Clear any previous messages
      const response = await probationaryAPI.getActiveProbationaryEmployees();
      const responseData = response.data.data || response.data;
      console.log('Loading active employees:', responseData); // Debug log
      setActiveEmployees(Array.isArray(responseData) ? responseData : []);
      setMessage('Active employees refreshed successfully');
      
      // Auto-clear success message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error loading active employees:', error);
      setMessage('Error loading active employees. Please try again.');
      setActiveEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUpcomingReviews = async () => {
    try {
      setLoading(true);
      setMessage(''); // Clear any previous messages
      const response = await probationaryAPI.getUpcomingReviews(30);
      const responseData = response.data.data || response.data;
      console.log('Loading upcoming reviews:', responseData); // Debug log
      setUpcomingReviews(Array.isArray(responseData) ? responseData : []);
      setMessage('Upcoming reviews refreshed successfully');
      
      // Auto-clear success message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error loading upcoming reviews:', error);
      setMessage('Error loading upcoming reviews. Please try again.');
      setUpcomingReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'overdue': 'bg-red-100 text-red-800',
      'completed': 'bg-green-100 text-green-800',
      'active': 'bg-blue-100 text-blue-800',
      'extended': 'bg-purple-100 text-purple-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Dashboard Header with Refresh Button */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-lg font-medium text-gray-900">Dashboard Overview</h2>
          <p className="mt-1 text-sm text-gray-700">
            Real-time overview of probationary employee status and upcoming reviews.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={loadDashboardData}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Dashboard
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div 
          className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => setActiveTab('active')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Probations</dt>
                  <dd className="text-lg font-medium text-gray-900">{dashboardData?.stats?.totalActiveProbation || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            setActiveTab('reviews');
            setReviewFilter('due');
          }}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Reviews Due</dt>
                  <dd className="text-lg font-medium text-gray-900">{dashboardData?.stats?.reviewsDueThisMonth || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            setActiveTab('reviews');
            setReviewFilter('overdue');
          }}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Overdue Reviews</dt>
                  <dd className="text-lg font-medium text-gray-900">{dashboardData?.stats?.reviewsOverdue || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            setActiveTab('reviews');
            setReviewFilter('completed');
          }}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Completed Reviews</dt>
                  <dd className="text-lg font-medium text-gray-900">{dashboardData?.stats?.completedReviews || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Employees */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Probationary Employees</h3>
        </div>
        <div className="p-6">
          {(dashboardData?.recentEmployees || []).length === 0 ? (
            <p className="text-gray-500 text-center py-4">No recent probationary employees found.</p>
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(dashboardData?.recentEmployees || []).map((employee) => (
                    <tr key={employee.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {employee.first_name} {employee.last_name}
                            </div>
                            <div className="text-sm text-gray-500">{employee.employee_number}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.department}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(employee.start_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(employee.current_end_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(employee.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderActiveEmployees = () => (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Active Probationary Employees</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage active probationary periods and track employee progress.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={loadActiveEmployees}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {activeEmployees.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No active probationary employees found.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(activeEmployees || []).map((employee) => (
                <tr key={employee.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {employee.first_name} {employee.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{employee.employee_number}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{employee.department}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.manager_first_name ? `${employee.manager_first_name} ${employee.manager_last_name}` : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(employee.start_date)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(employee.current_end_date)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(employee.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  // Filter reviews based on current filter
  const getFilteredReviews = () => {
    if (!upcomingReviews || upcomingReviews.length === 0) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return upcomingReviews.filter(review => {
      const dueDate = new Date(review.due_date);
      dueDate.setHours(0, 0, 0, 0);
      
      switch (reviewFilter) {
        case 'due':
          return review.urgency_status === 'pending' && dueDate >= today;
        case 'overdue':
          return review.urgency_status === 'overdue' || dueDate < today;
        case 'completed':
          return review.status === 'completed';
        default:
          return true; // 'all' - show everything
      }
    });
  };

  const getFilterTitle = () => {
    switch (reviewFilter) {
      case 'due':
        return 'Reviews Due';
      case 'overdue':
        return 'Overdue Reviews';
      case 'completed':
        return 'Completed Reviews';
      default:
        return 'Upcoming Reviews';
    }
  };

  const getFilterDescription = () => {
    switch (reviewFilter) {
      case 'due':
        return 'Reviews that are currently due and not overdue.';
      case 'overdue':
        return 'Reviews that are past their due date.';
      case 'completed':
        return 'Recently completed probationary reviews.';
      default:
        return 'Reviews that are due within the next 30 days.';
    }
  };

  const renderUpcomingReviews = () => {
    const filteredReviews = getFilteredReviews();
    
    return (
      <div className="space-y-6">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-semibold text-gray-900">{getFilterTitle()}</h1>
            <p className="mt-2 text-sm text-gray-700">
              {getFilterDescription()}
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex gap-2">
            <select
              value={reviewFilter}
              onChange={(e) => setReviewFilter(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Reviews</option>
              <option value="due">Due</option>
              <option value="overdue">Overdue</option>
              <option value="completed">Completed</option>
            </select>
            <button
              type="button"
              onClick={loadUpcomingReviews}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {filteredReviews.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">
              {reviewFilter === 'all' 
                ? 'No upcoming reviews found.' 
                : `No ${reviewFilter} reviews found.`
              }
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Review Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(filteredReviews || []).map((review) => (
                <tr key={review.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {review.first_name} {review.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{review.employee_number}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="capitalize text-sm text-gray-900">{review.review_type}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(review.due_date)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {review.document_uploaded ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Uploaded
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(review.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'active':
        return renderActiveEmployees();
      case 'reviews':
        return renderUpcomingReviews();
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Probationary Management</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage employee probationary periods, reviews, and documentation.
          </p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-md ${message.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            <p>{message}</p>
            <button onClick={() => setMessage('')} className="float-right text-lg">&times;</button>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Tab Content */}
        {!loading && renderTabContent()}
      </div>
    </div>
  );
};

export default ProbationaryManagement;